import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { CHANGE_CONTROL_KEY } from '../change-control/queries';
import type { ChangeControlRunResponse } from '../change-control/types';
import { isTerminalStatus } from '../change-control/types';
import { JOB_CARDS_KEY } from '../job-cards/queries';
import { OPERATIONS_KEY } from '../operations/queries';
import { PRODUCTION_PLANNER_KEY } from '../production-planner/queries';
import { RUN_SHEETS_KEY } from '../run-sheets/queries';
import type { RunSheetsMatrixResponse } from '../run-sheets/types';
import { getClientId } from './client-id';
import { useRealtimeSocket } from './realtime-provider';
import type { RunSheetChangedMessage, OperationsChangedMessage, RunChangedMessage } from './types';

interface UseChangeControlRealtimeOptions {
  /** Feature-flag gate. When false the hook no-ops and opens no socket. */
  enabled: boolean;
}

/** Trailing-edge coalescing window for run-sheet/operations staleness signals. */
const COALESCE_MS = 500;

/**
 * Merge a `run_changed` payload onto a cached `ChangeControlRunResponse`.
 *
 * Guards against out-of-order / late deliveries:
 *  - never overwrite a cached TERMINAL status with a non-terminal one (a late
 *    throttled `processing` UPDATE must not clobber a `completed` we already saw),
 *  - never let the `completed` / `failed` counters go backwards.
 */
function mergeRunProgress(
  prev: ChangeControlRunResponse,
  msg: RunChangedMessage,
): ChangeControlRunResponse {
  const keepStatus = isTerminalStatus(prev.status) && !isTerminalStatus(msg.status);
  return {
    ...prev,
    status: keepStatus ? prev.status : msg.status,
    kind: msg.kind,
    total: msg.total,
    completed: Math.max(prev.completed, msg.completed),
    failed: Math.max(prev.failed, msg.failed),
    target_version: msg.target_version,
    bill_of_operations_id: msg.bill_of_operations_id,
    operation_id: msg.operation_id,
  };
}

/**
 * Subscribes the session to `change_control:<partnerId>` for the whole app
 * lifetime and keeps the change-control / Bill of Operations / operations caches
 * live:
 *
 *  - `run_changed` patches the run-detail, runs-list and matrix caches in place
 *    for smooth progress (`Releasing… N/M`), and invalidates the full set on
 *    INSERT or terminal transitions so lists / counts / behind-work-orders
 *    reconcile against the server.
 *  - `boo_changed` / `operations_changed` are staleness pings (matrix cell
 *    edits, rule edits, operation commits from OTHER tabs); each is coalesced
 *    through a single trailing-edge timer into one batch of invalidations. The
 *    writing tab skips the echo of its own edit via `origin_client_id`.
 *
 * Shares the ref-counted socket with the other realtime consumers via
 * `RealtimeProvider`. No-ops (and opens no socket) when disabled or without a
 * partner id.
 */
export function useChangeControlRealtime(
  partnerId: string | undefined,
  { enabled }: UseChangeControlRealtimeOptions,
): void {
  const queryClient = useQueryClient();
  const { acquireSocket, releaseSocket } = useRealtimeSocket();

  useEffect(() => {
    if (!enabled || !partnerId) return;

    const socket = acquireSocket();
    if (!socket) return;

    const chan = socket.channel(`change_control:${partnerId}`, {});

    // ── run_changed ──────────────────────────────────────────────────
    const runRef = chan.on('run_changed', (msg: RunChangedMessage) => {
      // Patch the run-detail cache — only when a value is already cached (a
      // brand-new run has none; the INSERT invalidation below fetches it).
      queryClient.setQueryData<ChangeControlRunResponse>(
        [CHANGE_CONTROL_KEY, 'run', msg.run_id],
        (prev) => (prev ? mergeRunProgress(prev, msg) : prev),
      );

      // Patch any cached runs list (all filter variants live under the prefix).
      queryClient.setQueriesData<ChangeControlRunResponse[]>(
        { queryKey: [CHANGE_CONTROL_KEY, 'runs'] },
        (list) =>
          list
            ? list.map((row) =>
                row.change_control_run_id === msg.run_id ? mergeRunProgress(row, msg) : row,
              )
            : list,
      );

      // Advance the matrix "Releasing… N/M" badge for products on this run.
      queryClient.setQueriesData<RunSheetsMatrixResponse>(
        { queryKey: [RUN_SHEETS_KEY, 'matrix'] },
        (data) => {
          if (!data) return data;
          let touched = false;
          const products = data.products.map((p) => {
            if (p.active_release_run_id !== msg.run_id) return p;
            touched = true;
            return {
              ...p,
              active_release_run_total: msg.total,
              active_release_run_completed: Math.max(
                p.active_release_run_completed ?? 0,
                msg.completed,
              ),
            };
          });
          return touched ? { ...data, products } : data;
        },
      );

      const terminal = isTerminalStatus(msg.status);
      if (msg.op === 'INSERT' || terminal) {
        // A run appeared or finished: reconcile lists, counts, the matrix
        // (active_release_run_id clears) and the behind-work-orders set.
        void queryClient.invalidateQueries({ queryKey: [CHANGE_CONTROL_KEY, 'runs'] });
        void queryClient.invalidateQueries({ queryKey: [CHANGE_CONTROL_KEY, 'run', msg.run_id] });
        void queryClient.invalidateQueries({
          queryKey: [CHANGE_CONTROL_KEY, 'run-items', msg.run_id],
        });
        void queryClient.invalidateQueries({ queryKey: [RUN_SHEETS_KEY, 'matrix'] });
        void queryClient.invalidateQueries({
          queryKey: [CHANGE_CONTROL_KEY, 'behind-work-orders'],
        });
        if (terminal) {
          // A completed run has rolled forward every affected Work Order's
          // job cards — refresh any open Production Planner / job-cards view
          // so it stops serving pre-release cycle time / headcount.
          void queryClient.invalidateQueries({ queryKey: [PRODUCTION_PLANNER_KEY] });
          void queryClient.invalidateQueries({ queryKey: [JOB_CARDS_KEY] });
        }
      } else {
        // Non-terminal UPDATE (server-throttled ≤2Hz): refresh per-work-order
        // rows so an open run-detail page advances. Only refetches if mounted.
        void queryClient.invalidateQueries({
          queryKey: [CHANGE_CONTROL_KEY, 'run-items', msg.run_id],
        });
      }
    });

    // ── boo_changed / operations_changed coalescers ──────────────────
    // A single pending timer each: further events while one is pending fold
    // into that one flush ("one pending flush for all events").
    let runSheetTimer: ReturnType<typeof setTimeout> | null = null;
    let opsTimer: ReturnType<typeof setTimeout> | null = null;

    const flushRunSheet = () => {
      runSheetTimer = null;
      void queryClient.invalidateQueries({ queryKey: [RUN_SHEETS_KEY, 'matrix'] });
      void queryClient.invalidateQueries({ queryKey: [RUN_SHEETS_KEY, 'rules-list'] });
      void queryClient.invalidateQueries({ queryKey: [OPERATIONS_KEY, 'rules'] });
      void queryClient.invalidateQueries({ queryKey: [OPERATIONS_KEY, 'run-sheets'] });
      void queryClient.invalidateQueries({ queryKey: [CHANGE_CONTROL_KEY, 'behind-work-orders'] });
    };

    const flushOps = () => {
      opsTimer = null;
      void queryClient.invalidateQueries({ queryKey: [OPERATIONS_KEY] });
      void queryClient.invalidateQueries({ queryKey: [RUN_SHEETS_KEY, 'matrix'] });
    };

    const runSheetRef = chan.on('boo_changed', (msg: RunSheetChangedMessage) => {
      if (msg.origin_client_id === getClientId()) return;
      if (runSheetTimer === null) runSheetTimer = setTimeout(flushRunSheet, COALESCE_MS);
    });

    const opsRef = chan.on('operations_changed', (msg: OperationsChangedMessage) => {
      if (msg.origin_client_id === getClientId()) return;
      if (opsTimer === null) opsTimer = setTimeout(flushOps, COALESCE_MS);
    });

    chan.join();

    return () => {
      chan.off('run_changed', runRef);
      chan.off('boo_changed', runSheetRef);
      chan.off('operations_changed', opsRef);
      if (runSheetTimer !== null) clearTimeout(runSheetTimer);
      if (opsTimer !== null) clearTimeout(opsTimer);
      chan.leave();
      releaseSocket();
    };
  }, [enabled, partnerId, acquireSocket, releaseSocket, queryClient]);
}
